import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { SEO } from '../components/SEO';
import { ContactDialog } from '../components/ContactDialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contacts = () => {
  const { i18n } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const lang = i18n.language;

  useEffect(() => {
    fetchPageContent();
    fetchSiteContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/pages-content`);
      if (response.data && response.data.pages && response.data.pages.contacts) {
        setPageContent(response.data.pages.contacts);
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
    }
  };

  const fetchSiteContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content`);
      setSiteContent(response.data);
    } catch (error) {
      console.error('Error fetching site content:', error);
    }
  };

  // Хелпер для получения текста с фоллбэком
  const getText = (field, fallbackRu, fallbackLv) => {
    if (pageContent && pageContent[field]) {
      if (typeof pageContent[field] === 'object') {
        return pageContent[field][lang] || pageContent[field].ru || (lang === 'lv' ? fallbackLv : fallbackRu);
      }
      return pageContent[field];
    }
    return lang === 'lv' ? fallbackLv : fallbackRu;
  };

  const phone = pageContent?.phone || siteContent?.contact_phone || '+371 28448991';
  const email = pageContent?.email || 'pure.atelier.riga@gmail.com';

  const contactInfo = [
    {
      icon: Phone,
      title: lang === 'lv' ? 'Tālrunis' : 'Телефон',
      value: phone,
      link: `tel:${phone}`
    },
    {
      icon: Mail,
      title: 'E-pasts / Email',
      value: email,
      link: `mailto:${email}`
    },
    {
      icon: MapPin,
      title: lang === 'lv' ? 'Atrašanās vieta' : 'Местоположение',
      value: getText('location', 'Рига, Латвия', 'Rīga, Latvija'),
      link: null
    },
    {
      icon: Clock,
      title: lang === 'lv' ? 'Darba laiks' : 'Часы работы',
      value: getText('working_hours', 'По предварительной записи', 'Pēc iepriekšējas vienošanās'),
      link: null
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="contacts-page">
      <SEO 
        title={getText('page_title', 'Контакты', 'Kontakti')}
        description={lang === 'lv'
          ? `Sazinieties ar Pure Atelier Riga: ${phone}, ${email}`
          : `Свяжитесь с Pure Atelier Riga: ${phone}, ${email}`}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl mb-6 tracking-tight" data-testid="contacts-title">
            {getText('page_title', 'Контакты', 'Kontakti')}
          </h1>
          <p className="font-body text-xl text-muted-foreground max-w-2xl mx-auto">
            {getText('page_description',
              'Свяжитесь с нами, чтобы обсудить ваше будущее украшение',
              'Sazināties ar mums, lai apspriestu jūsu nākamo juvelierizstrādājumu'
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="bg-background p-6 rounded-sm border border-border hover:border-accent/50 transition-colors duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-body font-medium text-sm uppercase tracking-wide text-muted-foreground mb-2">
                        {info.title}
                      </h3>
                      {info.link ? (
                        <a 
                          href={info.link}
                          className="font-body text-lg hover:text-accent transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-body text-lg">{info.value}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-accent/5 to-transparent p-12 rounded-sm border border-accent/20 flex flex-col justify-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl mb-6 tracking-tight">
              {getText('form_title', 'Связаться с нами', 'Sazināties')}
            </h2>
            <p className="font-body text-lg text-muted-foreground mb-8 leading-relaxed">
              {getText('form_description',
                'Заполните контактную форму, и мы свяжемся с вами в течение 24 часов, чтобы обсудить ваше уникальное украшение.',
                'Aizpildiet kontaktformu, un mēs sazināsimies ar jums 24 stundu laikā, lai apspriestu jūsu unikālo juvelierizstrādājumu.'
              )}
            </p>
            <Button 
              size="lg"
              onClick={() => setContactOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
              data-testid="open-contact-form-button"
            >
              {lang === 'lv' ? 'Atvērt formu' : 'Открыть форму'}
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-background p-12 rounded-sm border border-border text-center"
        >
          <h2 className="font-heading text-2xl md:text-3xl mb-4 tracking-tight">
            {getText('visit_title', 'Посещение по предварительной записи', 'Apmeklēšana pēc iepriekšējas vienošanās')}
          </h2>
          <p className="font-body text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {getText('visit_description',
              'Мы работаем индивидуально с каждым клиентом. Пожалуйста, свяжитесь с нами, чтобы договориться о встрече и посмотреть наши украшения лично.',
              'Mēs strādājam individuāli ar katru klientu. Lūdzu, sazinieties ar mums, lai vienotos par tikšanos un apskatītu mūsu juvelierizstrādājumus personīgi.'
            )}
          </p>
        </motion.div>
      </div>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
};
